import React, { useCallback, useEffect, useMemo, useState } from 'react';
import texasApi from '../../services/texasApi';
import { landlordApi } from '../../services/api';

/**
 * US-104 — Houston ASEC Remediation page (route /texas/asec).
 *
 * Brief (m-20260930T193000Z): this is the specific page the human reported
 * dead ("Page implementation pending — F-291"). Backend DONE (F-143, 9
 * endpoints); texasApi.houstonAsec.* client exists — use it, no new client.
 *
 * Rules honoured:
 * - Pickers and countdown are DRIVEN from GET /meta (departments,
 *   violationTypes, severityDeadlineDays, caseStatuses, departmentKeys,
 *   statutoryReference, closureRule, evidenceTypes). No hardcoded Houston
 *   department lists — that is the point of the meta endpoint.
 * - The citation -> inspection -> remediation plan -> progress -> signoff ->
 *   close chain is surfaced, and the all-departments signoff rule
 *   (meta.closureRule) is visible: a case that looks closable but is not is
 *   a compliance trap.
 * - Remediation deadline display is a consumer of the F-135 expiresAt field
 *   (already epoch-seconds on stored items); the FE never reimplements TTL.
 */

interface DepartmentMeta {
  key: string;
  name: string;
  scope?: string;
  ordinanceReference?: string;
  signOffAuthority?: string;
}

interface ViolationTypeMeta {
  type: string;
  department: string;
  severity: string;
  title: string;
  deadlineDays: number;
}

interface AsecMeta {
  departments: DepartmentMeta[];
  violationTypes: ViolationTypeMeta[];
  severityDeadlineDays: Record<string, number>;
  caseStatuses: string[];
  departmentKeys: string[];
  statutoryReference: string;
  closureRule: string;
  evidenceTypes: string[];
}

interface Citation {
  propertyId: string;
  citationNumber?: string;
  issueDate?: string;
  abatementDeadline?: string;
  status?: string;
  department?: string;
  violationType?: string;
  expiresAt?: number;
  [key: string]: unknown;
}

const statusTone = (status?: string): string => {
  switch (status) {
    case 'closed':
    case 'completed':
      return 'bg-emerald-100 text-emerald-700';
    case 'pending_remediation':
    case 'under-remediation':
    case 'in-progress':
      return 'bg-amber-100 text-amber-700';
    case 'awaiting_signoff':
    case 'pending-signoff':
      return 'bg-sky-100 text-sky-700';
    case 'scheduled':
      return 'bg-indigo-100 text-indigo-700';
    default:
      return 'bg-red-100 text-red-700'; // open and unknown
  }
};

const fmtDate = (value?: string): string => {
  if (!value) return '—';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? String(value) : d.toISOString().slice(0, 10);
};

/** F-135 expiresAt is epoch seconds; render days remaining without reimplementing TTL. */
const daysUntilExpiresAt = (expiresAt?: number): number | null => {
  if (typeof expiresAt !== 'number') return null;
  return Math.ceil((expiresAt * 1000 - Date.now()) / 86400000);
};

interface PropertyOption {
  id: string;
  name: string;
}

export const AsecRemediationPage: React.FC = () => {
  const [meta, setMeta] = useState<AsecMeta | null>(null);
  const [properties, setProperties] = useState<PropertyOption[]>([]);
  const [selectedId, setSelectedId] = useState<string>('');
  const [citations, setCitations] = useState<Citation[]>([]);
  const [signedOff, setSignedOff] = useState<Record<string, boolean>>({});
  const [loading, setLoading] = useState<boolean>(false);
  const [actionMsg, setActionMsg] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [metaError, setMetaError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await texasApi.houstonAsec.getMeta();
        const d = (res?.data?.data ?? res?.data) as AsecMeta;
        if (!cancelled && d && typeof d === 'object') setMeta(d);
      } catch {
        if (!cancelled) setMetaError('Failed to load ASEC metadata');
      }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await landlordApi.get('api/properties');
        const d = res?.data?.data ?? res?.data;
        const rows: any[] = Array.isArray(d) ? d : Array.isArray(d?.properties) ? d.properties : [];
        if (cancelled) return;
        const options = rows.map((p: any) => ({
          id: String(p.id ?? p.propertyId ?? p.propertyID ?? ''),
          name: String(p.name ?? p.propertyName ?? p.address ?? p.id ?? 'Property'),
        })).filter((p) => p.id);
        setProperties(options);
        if (options.length > 0) setSelectedId((prev) => prev || options[0].id);
      } catch {
        if (!cancelled) setError('Failed to load properties');
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const loadCase = useCallback(async (propertyId: string) => {
    if (!propertyId) return;
    setLoading(true);
    setError(null);
    try {
      const rows = await texasApi.houstonAsec.getCitations(propertyId);
      setCitations(Array.isArray(rows) ? rows : []);
    } catch (err: any) {
      // F-144 (human-reported): a 404 here is the "no case filed yet" state, not
      // a dead end. Show the empty state instead of an error. (Pre-F-143 backends
      // return 404 for an empty in-memory store; post-F-143 they return a
      // NO_CASE empty-case record, which dataArray unwraps to [].)
      const status = err?.response?.status;
      if (status === 404) {
        setError(null);
        setCitations([]);
      } else {
        setError('Failed to load ASEC citations');
        setCitations([]);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadCase(selectedId); }, [selectedId, loadCase]);

  const openCitations = useMemo(
    () => citations.filter((c) => c.status !== 'closed' && c.status !== 'completed'),
    [citations]
  );

  const departmentsOnCase = useMemo(() => {
    const keys = new Set<string>();
    citations.forEach((c) => {
      const k = (c.department as string) || ((c as any).violationMeta?.department as string);
      if (typeof k === 'string' && k) keys.add(k);
    });
    return [...keys];
  }, [citations]);

  const caseStatus = useMemo(() => {
    if (citations.length === 0) return 'no-case';
    if (citations.every((c) => c.status === 'closed' || c.status === 'completed')) return 'closed';
    if (citations.some((c) => c.status === 'open' || c.status === 'scheduled')) return 'open';
    return 'under-remediation';
  }, [citations]);

  const run = async (fn: () => Promise<unknown>, label: string) => {
    setActionMsg(null);
    setError(null);
    try {
      await fn();
      setActionMsg(label);
      if (selectedId) await loadCase(selectedId);
    } catch {
      setError(`${label} failed`);
    }
  };

  const handleRecordCitations = () => {
    if (!selectedId || !meta) return;
    const first = meta.violationTypes?.[0];
    void run(
      () => texasApi.houstonAsec.recordCitations({
        propertyId: selectedId,
        citations: [{
          violationType: first?.type,
          department: first?.department,
          severity: first?.severity,
          description: first?.title,
        }],
      }),
      'Citation recorded'
    );
  };

  const handleScheduleInspection = () => {
    if (!selectedId) return;
    void run(
      () => texasApi.houstonAsec.recordInspection({
        propertyId: selectedId,
        departments: departmentsOnCase.length > 0 ? departmentsOnCase : undefined,
        scheduledFor: new Date().toISOString(),
      }),
      'Inspection scheduled'
    );
  };

  const handleCompleteInspection = () => {
    if (!selectedId) return;
    void run(
      () => texasApi.houstonAsec.completeInspection({
        propertyId: selectedId,
        inspectionId: `INSP-${selectedId}-${Date.now()}`,
        outcome: 'failed',
        notes: 'Completed from ASEC remediation page',
      }),
      'Inspection completed'
    );
  };

  const handleCreatePlan = () => {
    if (!selectedId) return;
    void run(
      () => texasApi.houstonAsec.createRemediationPlan({
        propertyId: selectedId,
        preparedBy: 'landlord',
        notes: 'Remediation plan created from ASEC remediation page',
      }),
      'Remediation plan created'
    );
  };

  const handleProgress = (citation: Citation) => {
    if (!selectedId) return;
    const id = String(citation.citationNumber ?? citation.id ?? '');
    void run(
      () => texasApi.houstonAsec.recordRemediationProgress({
        propertyId: selectedId,
        citationId: id,
        status: 'in-progress',
        note: 'Progress recorded from ASEC remediation page',
      }),
      'Progress recorded'
    );
  };

  const handleSignOff = (departmentKey: string) => {
    if (!selectedId) return;
    void run(
      () => texasApi.houstonAsec.signOff({
        propertyId: selectedId,
        department: departmentKey,
        signedOffBy: 'landlord',
        notes: 'Sign-off recorded from ASEC remediation page',
      }),
      `Sign-off recorded for ${departmentKey}`
    ).then(() => {
      setSignedOff((prev) => ({ ...prev, [departmentKey]: true }));
    });
  };

  const handleClose = () => {
    if (!selectedId) return;
    void run(
      () => texasApi.houstonAsec.close({
        propertyId: selectedId,
        closedBy: 'landlord',
        notes: 'Case closed from ASEC remediation page',
      }),
      'Case closed'
    );
  };

  const allDepartmentsSigned = departmentsOnCase.length > 0
    && departmentsOnCase.every((k) => signedOff[k]);

  return (
    <div data-testid="asec-remediation-page" className="texas-page-container">
      <h1>Houston ASEC Remediation</h1>
      <p className="page-description">
        Multi-department citation tracking and remediation coordination.
        {meta?.statutoryReference ? ` ${meta.statutoryReference}.` : ''}
      </p>

      {meta?.closureRule && (
        <div
          data-testid="asec-closure-rule"
          className="mb-4 p-3 rounded-lg border border-sky-200 bg-sky-50 text-xs text-sky-900"
        >
          <span className="font-bold">Closure rule:</span> {meta.closureRule}
        </div>
      )}

      <section className="bg-white rounded-xl border border-gray-200 shadow-sm p-4 mb-4">
        <div className="flex items-center gap-3 flex-wrap">
          <label htmlFor="asec-property" className="text-xs font-bold text-slate-500 tracking-wider">PROPERTY</label>
          <select
            id="asec-property"
            data-testid="asec-property-select"
            value={selectedId}
            onChange={(e) => { setSelectedId(e.target.value); setSignedOff({}); }}
            className="px-3 py-2 border border-gray-300 rounded text-sm bg-white"
          >
            {properties.length === 0 && <option value="">No properties</option>}
            {properties.map((p) => (
              <option key={p.id} value={p.id}>{p.name}</option>
            ))}
          </select>
          {caseStatus !== 'no-case' && (
            <span
              data-testid="asec-case-status"
              className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${statusTone(caseStatus)}`}
            >
              {meta?.caseStatuses?.includes(caseStatus) ? caseStatus : caseStatus.toUpperCase()}
            </span>
          )}
        </div>
      </section>

      {actionMsg && <div className="success" data-testid="asec-action-msg">{actionMsg}</div>}
      {metaError && <div className="error" data-testid="asec-meta-error">{metaError}</div>}
      {error && <div className="error" data-testid="error">{error}</div>}

      <section className="bg-white rounded-xl border border-gray-200 shadow-sm p-4 mb-4" data-testid="asec-citations">
        <h2>Citations</h2>
        {loading ? (
          <div className="no-results">Loading citations…</div>
        ) : citations.length === 0 ? (
          <div className="no-results">No citations on file for this property.</div>
        ) : (
          <div className="space-y-2">
            {citations.map((c, idx) => {
              const days = daysUntilExpiresAt(c.expiresAt);
              const deptKey = (c.department as string) || '';
              const dept = meta?.departments?.find((d) => d.key === deptKey);
              return (
                <div
                  key={String(c.citationNumber ?? c.id ?? idx)}
                  data-testid="asec-citation-row"
                  className="p-3 bg-gray-50 rounded-lg border border-gray-200/80"
                >
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <div>
                      <div className="font-bold text-gray-900 text-xs">
                        {String(c.citationNumber ?? c.violationType ?? 'Citation')}
                        {c.violationType ? ` — ${String(c.violationType)}` : ''}
                      </div>
                      <div className="text-xs text-slate-500">
                        {dept ? `${dept.name}` : (deptKey || 'Department unknown')}
                        {' · issued '}{fmtDate(c.issueDate)}
                        {c.abatementDeadline ? ` · abate by ${fmtDate(c.abatementDeadline)}` : ''}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {days !== null && (
                        <span
                          data-testid="asec-expires-countdown"
                          className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${days <= 0 ? 'bg-red-100 text-red-700' : days <= 7 ? 'bg-amber-100 text-amber-700' : 'bg-emerald-100 text-emerald-700'}`}
                        >
                          {days <= 0 ? 'expired' : `${days}d left`}
                        </span>
                      )}
                      <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${statusTone(c.status)}`}>
                        {c.status ?? 'open'}
                      </span>
                    </div>
                  </div>
                  {c.status !== 'closed' && c.status !== 'completed' && (
                    <div className="mt-2">
                      <button
                        onClick={() => handleProgress(c)}
                        data-testid="asec-progress-btn"
                        className="px-3 py-1.5 bg-slate-800 text-white rounded text-xs hover:bg-slate-700"
                      >
                        Record progress
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </section>

      <section className="bg-white rounded-xl border border-gray-200 shadow-sm p-4 mb-4" data-testid="asec-workflow">
        <h2>Workflow</h2>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={handleRecordCitations}
            disabled={!selectedId || !meta}
            data-testid="asec-cite-btn"
            className="px-4 py-2 bg-slate-800 text-white rounded text-sm hover:bg-slate-700 disabled:opacity-50"
          >
            Record citation
          </button>
          <button
            onClick={handleScheduleInspection}
            disabled={!selectedId}
            data-testid="asec-inspect-btn"
            className="px-4 py-2 bg-slate-800 text-white rounded text-sm hover:bg-slate-700 disabled:opacity-50"
          >
            Schedule inspection
          </button>
          <button
            onClick={handleCompleteInspection}
            disabled={!selectedId}
            data-testid="asec-inspect-complete-btn"
            className="px-4 py-2 bg-slate-800 text-white rounded text-sm hover:bg-slate-700 disabled:opacity-50"
          >
            Complete inspection
          </button>
          <button
            onClick={handleCreatePlan}
            disabled={!selectedId}
            data-testid="asec-plan-btn"
            className="px-4 py-2 bg-slate-800 text-white rounded text-sm hover:bg-slate-700 disabled:opacity-50"
          >
            Create remediation plan
          </button>
        </div>
      </section>

      <section className="bg-white rounded-xl border border-gray-200 shadow-sm p-4 mb-4" data-testid="asec-signoffs">
        <h2>Department sign-offs</h2>
        {openCitations.length === 0 && citations.length > 0 ? (
          <div className="no-results">All citations remediated — case can be closed.</div>
        ) : departmentsOnCase.length === 0 ? (
          <div className="no-results">No departments involved yet.</div>
        ) : (
          <div className="space-y-2">
            {departmentsOnCase.map((k) => {
              const dept = meta?.departments?.find((d) => d.key === k);
              return (
                <div key={k} className="p-3 bg-gray-50 rounded-lg border border-gray-200/80 flex items-center justify-between flex-wrap gap-2">
                  <div>
                    <div className="font-bold text-gray-900 text-xs">{dept?.name ?? k}</div>
                    {dept?.signOffAuthority && (
                      <div className="text-xs text-slate-500">Sign-off authority: {dept.signOffAuthority}</div>
                    )}
                  </div>
                  <button
                    onClick={() => handleSignOff(k)}
                    disabled={!!signedOff[k]}
                    data-testid={`asec-signoff-${k}`}
                    className="px-3 py-1.5 bg-slate-800 text-white rounded text-xs hover:bg-slate-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {signedOff[k] ? 'Signed off' : 'Record sign-off'}
                  </button>
                </div>
              );
            })}
          </div>
        )}
        {citations.length > 0 && (
          <div className="mt-3">
            <button
              onClick={handleClose}
              disabled={!allDepartmentsSigned}
              data-testid="asec-close-btn"
              title={allDepartmentsSigned ? 'Close case' : (meta?.closureRule ?? 'All departments must sign off first')}
              className="px-4 py-2 bg-emerald-700 text-white rounded text-sm hover:bg-emerald-600 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Close case
            </button>
            {!allDepartmentsSigned && (
              <span className="text-xs text-slate-500 ml-2">
                All departments holding citations must sign off before closure.
              </span>
            )}
          </div>
        )}
      </section>

      {meta?.evidenceTypes && meta.evidenceTypes.length > 0 && (
        <section className="bg-white rounded-xl border border-gray-200 shadow-sm p-4" data-testid="asec-evidence">
          <h2>Expected evidence types</h2>
          <ul className="space-y-1">
            {meta.evidenceTypes.map((t) => (
              <li key={t} className="text-xs text-slate-600">{t}</li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
};

export default AsecRemediationPage;
